import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  PreAlert,
  PreAlertDetail,
  PaginatedResponse,
  ApiResponse,
  PreAlertQueryParams,
  PreAlertSearchFilter,
  ExportHistoryItem,
  ShopeeOrder
} from '../../models/pre-alert.models';

@Injectable({
  providedIn: 'root'
})
export class PreAlertService {
  private apiUrl = `${environment.apiUrl}/pre-alerts`;

  constructor(private http: HttpClient) {}

  /**
   * Get paginated list of pre-alerts with optional filtering
   */
  getPreAlerts(filter?: PreAlertSearchFilter): Observable<PaginatedResponse<PreAlert>> {
    let params = new HttpParams();

    // Always set required parameters with defaults
    params = params.set('PageNumber', filter?.page?.toString() || '1');
    params = params.set('PageSize', filter?.page_size?.toString() || '20');
    params = params.set('Status', filter?.status || '');
    params = params.set('SearchTerm', '');

    if (filter) {
      if (filter.status) params = params.set('Status', filter.status);
      if (filter.lading_bill || filter.transport_company || filter.vessel_no || 
          filter.arrival_location || filter.departure_location) {
        // Build search term from multiple filters
        const searchTerms: string[] = [];
        if (filter.lading_bill) searchTerms.push(filter.lading_bill);
        if (filter.transport_company) searchTerms.push(filter.transport_company);
        if (filter.vessel_no) searchTerms.push(filter.vessel_no);
        if (filter.arrival_location) searchTerms.push(filter.arrival_location);
        if (filter.departure_location) searchTerms.push(filter.departure_location);
        
        if (searchTerms.length > 0) {
          params = params.set('SearchTerm', searchTerms.join(' '));
        }
      }
    }

    return this.http.get<PaginatedResponse<PreAlert>>(this.apiUrl, { params });
  }

  /**
   * Get detailed information about a specific pre-alert by lading bill
   */
  getPreAlertByLadingBill(ladingBill: string): Observable<ApiResponse<PreAlertDetail>> {
    return this.http.get<ApiResponse<PreAlertDetail>>(`${this.apiUrl}/${encodeURIComponent(ladingBill)}`);
  }

  /**
   * Check if a pre-alert exists by lading bill
   * Returns true if exists, false if not found
   */
  checkPreAlertExists(ladingBill: string): Observable<boolean> {
    return this.http.get<ApiResponse<PreAlertDetail>>(`${this.apiUrl}/${encodeURIComponent(ladingBill)}`).pipe(
      map(response => !!response.data),
      catchError(() => of(false))
    );
  }

  /**
   * Get cartons (ShopeeOrders) for a specific pre-alert
   */
  getPreAlertCartons(ladingBill: string): Observable<ShopeeOrder[]> {
    return this.http.get<ShopeeOrder[]>(`${this.apiUrl}/${encodeURIComponent(ladingBill)}/cartons`);
  }

  /**
   * Search pre-alerts with flexible criteria
   */
  searchPreAlerts(searchTerm: string): Observable<PaginatedResponse<PreAlert>> {
    const params = new HttpParams()
      .set('SearchTerm', searchTerm)
      .set('PageNumber', '1')
      .set('PageSize', '50');
    
    return this.http.get<PaginatedResponse<PreAlert>>(this.apiUrl, { params });
  }

  /**
   * Update pre-alert status
   */
  updatePreAlertStatus(ladingBill: string, status: string): Observable<ApiResponse> {
    const body = { status };
    return this.http.put<ApiResponse>(`${this.apiUrl}/${encodeURIComponent(ladingBill)}/status`, body);
  }

  /**
   * Delete pre-alert and all associated data
   */
  deletePreAlert(ladingBill: string): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this.apiUrl}/${encodeURIComponent(ladingBill)}`);
  }

  /**
   * Update pre-alert information
   */
  updatePreAlert(updateData: any): Observable<ApiResponse> {
    return this.http.put<ApiResponse>(`${this.apiUrl}/${encodeURIComponent(updateData.ladingBill)}`, updateData);
  }

  /**
   * Delete a specific carton from pre-alert
   */
  deleteCarton(ladingBill: string, trackingNumber: string): Observable<ApiResponse> {
    return this.http.delete<ApiResponse>(`${this.apiUrl}/${encodeURIComponent(ladingBill)}/cartons/${encodeURIComponent(trackingNumber)}`);
  }

  /**
   * Export pre-alerts data (if implemented on backend)
   */
  exportPreAlerts(filter?: PreAlertSearchFilter): Observable<Blob> {
    let params = new HttpParams();

    if (filter) {
      if (filter.status) params = params.set('Status', filter.status);
      if (filter.lading_bill) params = params.set('SearchTerm', filter.lading_bill);
      // Add other filters as needed
    }

    return this.http.get(`${this.apiUrl}/export`, {
      params,
      responseType: 'blob'
    });
  }

  /**
   * Export LM Warehouse data for a specific pre-alert
   */
  exportLMWarehouse(ladingBill: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${encodeURIComponent(ladingBill)}/export/lm-warehouse`, {
      responseType: 'blob'
    });
  }

  /**
   * Export Truck Loading data for a specific pre-alert
   */
  exportTruckLoading(ladingBill: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${encodeURIComponent(ladingBill)}/export/truck-loading`, {
      responseType: 'blob'
    });
  }

  /**
   * Get export history for a specific pre-alert
   */
  getExportHistory(ladingBill: string): Observable<ApiResponse<ExportHistoryItem[]>> {
    return this.http.get<ApiResponse<ExportHistoryItem[]>>(`${this.apiUrl}/${encodeURIComponent(ladingBill)}/export-history`);
  }

  /**
   * Helper method to download blob as file
   */
  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Helper method to generate filename for export
   */
  generateExportFilename(ladingBill: string, type: 'LM-Warehouse' | 'Truck-Loading'): string {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    return `${ladingBill}_${type}_${timestamp}.xlsx`;
  }

  /**
   * Helper method to format date for display
   */
  formatDate(date: Date | string): string {
    if (!date) return 'N/A';
    
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(d);
  }

  /**
   * Helper method to format weight for display
   */
  formatWeight(weight: number): string {
    if (weight === null || weight === undefined) return 'N/A';
    return `${weight.toFixed(2)} kg`;
  }

  /**
   * Helper method to calculate days until ETA
   */
  getDaysUntilETA(eta: Date | string): number {
    if (!eta) return 0;
    
    const etaDate = typeof eta === 'string' ? new Date(eta) : eta;
    const today = new Date();
    const diffTime = etaDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Helper method to get ETA status (upcoming, overdue, arrived)
   */
  getETAStatus(eta: Date | string): 'upcoming' | 'today' | 'overdue' | 'arrived' {
    const daysUntil = this.getDaysUntilETA(eta);
    
    if (daysUntil < 0) return 'overdue';
    if (daysUntil === 0) return 'today';
    if (daysUntil <= 7) return 'upcoming';
    return 'arrived';
  }

  /**
   * Get status badge color class
   */
  getStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'pending': return 'warning';
      case 'intransit': return 'info';
      case 'arrived': return 'success';
      case 'cleared': return 'primary';
      case 'delivered': return 'dark';
      default: return 'secondary';
    }
  }
}
